/**
 * The requirement form's submit rule, as plain functions.
 *
 * Two decisions live here, and both were wrong in the first version:
 *
 *  1. WHICH FIELDS MUST BE FILLED. The pack math needs its specification in one
 *     go, so a required field left blank blocks Confirm. But a field the pack
 *     math DERIVES is not part of that specification — `parallel_count` is the
 *     standing case: omit it and p follows from the energy/power targets. The
 *     form demanded every field regardless, so a label reading "leave blank =
 *     auto" left the customer staring at a dead button (BA, staging 2026-08-10).
 *
 *  2. WHAT A BLANK MEANS ON THE WIRE. Nothing. A blank optional field is left
 *     out of the answer entirely rather than sent as an empty value — "let the
 *     pack math decide" is the ABSENCE of a requirement, not a requirement
 *     without a number, and the prompt rule for parallel_count says exactly
 *     that: do not put it in the requirements at all.
 *
 * A third decision joined them on 2026-09-07: a 'choice' field carries its own
 * `options` and renders as a dropdown. The playbooks describe their cell-format
 * and chemistry questions as pick-one fields with alternatives, but until then
 * the field had nowhere to put them — it rendered as a bare text box pre-filled
 * with the default, and the alternatives had to be spelled out in the label.
 * A dropdown always holds a value, so it counts as filled; its answer is the
 * option's `value` on the wire and its `label` in the echo, the same split the
 * radio buttons of a single question make.
 *
 * Extracted from AskUserDialog so the rule can be tested against inputs instead
 * of asserted against the component's source text.
 */

const filled = (values, slug) => String(values?.[slug] ?? '').trim() !== ''

/**
 * The dropdown alternatives of a 'choice' field, or null when the field is a
 * typed or free-text field. A field is a dropdown when it carries at least two
 * options and its widget is 'choice' — an omitted `input` means 'choice', exactly
 * as it does on the backend. A 'choice' field WITHOUT options (a payload from
 * before per-field options existed) stays a text box, so nothing dead-ends.
 *
 * @param {{input?: {type?: string}, options?: Array<{value: string, label?: string}>}} field
 * @returns {Array<{value: string, label?: string}> | null}
 */
export function fieldSelectOptions(field) {
  const opts = Array.isArray(field?.options) ? field.options : []
  if (opts.length < 2) return null
  const type = field?.input?.type
  if (type && type !== 'choice') return null
  return opts
}

/**
 * The value a field starts with. A dropdown starts on the option whose `value`
 * equals `default` (else the first option — a select never sits on nothing);
 * every other field starts on its default as text, or blank.
 *
 * @param {{default?: unknown, input?: {type?: string}, options?: Array<{value: string}>}} field
 * @returns {string}
 */
export function initialFieldValue(field) {
  const opts = fieldSelectOptions(field)
  if (opts) {
    const wanted = field.default != null ? String(field.default) : null
    const match = wanted != null ? opts.find((o) => String(o.value) === wanted) : null
    return String((match || opts[0]).value)
  }
  return field?.default != null ? String(field.default) : ''
}

/**
 * May the form be submitted?
 *
 * A dropdown field always holds a value (see `initialFieldValue`), so it is
 * always filled — only text and number fields can block Confirm.
 *
 * @param {Array<{slug: string, optional?: boolean}>} fields
 * @param {Record<string, unknown>} values
 */
export function isFormComplete(fields, values) {
  if (!Array.isArray(fields) || fields.length === 0) return false
  return (
    fields.every((f) => f.optional || filled(values, f.slug)) &&
    // An all-optional form left entirely blank would submit an empty message,
    // which the send path drops — the dialog would simply stop responding.
    fields.some((f) => filled(values, f.slug))
  )
}

/**
 * The message to send, and the echo shown in the collapsed card.
 *
 * `wire` is `slug: value unit` pairs rather than a bare list, so the model maps
 * each number to the requirement it answers instead of guessing from the order.
 * `shown` uses the labels — the customer reads their own words back.
 *
 * For a dropdown field the wire carries the picked option's `value` and the
 * echo its `label` — the same split a single question's radio buttons make, so
 * "18650 only" is what the customer reads and "18650" is what the model gets.
 *
 * @param {Array<{slug: string, label: string, input?: {type?: string, unit?: string}, options?: Array<{value: string, label?: string}>, optional?: boolean}>} fields
 * @param {Record<string, unknown>} values
 * @returns {{wire: string, shown: string}}
 */
export function buildFormAnswer(fields, values) {
  const parts = (fields || [])
    .filter((f) => filled(values, f.slug))
    .map((f) => {
      const value = String(values[f.slug]).trim()
      const opts = fieldSelectOptions(f)
      if (opts) {
        const picked = opts.find((o) => String(o.value) === value)
        const wireValue = picked ? String(picked.value) : value
        const label = picked?.label ? String(picked.label) : wireValue
        return { wire: `${f.slug}: ${wireValue}`, shown: `${f.label} ${label}` }
      }
      const unit = f.input?.unit ? ` ${f.input.unit}` : ''
      return { wire: `${f.slug}: ${value}${unit}`, shown: `${f.label} ${value}${unit}` }
    })
  return {
    wire: parts.map((p) => p.wire).join('; '),
    shown: parts.map((p) => p.shown).join(' · '),
  }
}
